import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, ArrowUpRight, MapPin, GraduationCap, Sparkles } from 'lucide-react';
import { useOnboarding } from '../onboarding/OnboardingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLLEGES = [
    {
        name: 'Ashoka University',
        location: 'Sonipat, IN',
        streams: ['Liberal Arts', 'Economics', 'CS'],
        tuition: '$8,400',
        rank: '01',
        img: 'https://images.unsplash.com/photo-1592280771190-3e2e4d571952?w=800&auto=format&fit=crop&q=70',
    },
    {
        name: 'Sciences Po',
        location: 'Paris, FR',
        streams: ['Political Sci.', 'International Aff.'],
        tuition: '$14,200',
        rank: '02',
        img: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&auto=format&fit=crop&q=70',
    },
    {
        name: 'ETH Zürich',
        location: 'Zürich, CH',
        streams: ['Engineering', 'CS', 'Physics'],
        tuition: '$1,600',
        rank: '03',
        img: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&auto=format&fit=crop&q=70',
    },
];

export default function CollegeFinder({ goTo }) {
    const [q, setQ] = useState('');
    const [results, setResults] = useState([]);
    const [region, setRegion] = useState('india');
    const [loading, setLoading] = useState(false);
    const { user } = useOnboarding();
    const firstName = user?.name?.split(' ')[0] || 'reader';

    const runSearch = useCallback(async (query) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/college-search`, { params: { q: query } });
            setResults(res.data.results || []);
            setRegion(res.data.region || 'india');
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        runSearch('');
    }, [runSearch]);

    // Deep link from header Smart Search: prefill + run a college query
    useEffect(() => {
        const onPrefill = (ev) => {
            const query = ev.detail?.query || '';
            setQ(query);
            runSearch(query);
        };
        window.addEventListener('college:prefill', onPrefill);
        // Consume pending deep link set before this section mounted (page-flip delay)
        try {
            const pending = sessionStorage.getItem('sf.deeplink.college');
            if (pending) {
                sessionStorage.removeItem('sf.deeplink.college');
                setQ(pending);
                runSearch(pending);
            }
        } catch {}
        return () => window.removeEventListener('college:prefill', onPrefill);
    }, [runSearch]);

    return (
        <div data-testid="section-home" className="space-y-12">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                    §I — College Finder
                </div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)] tabular-nums">
                    12.2026
                </div>
            </div>

            {/* KINETIC HERO */}
            <div className="space-y-3">
                <div className="mono text-[11px] text-[color:var(--moss-deep)]">
                    {user ? `Prepared for your ${user.education || 'study'} goals.` : 'A field-guide for the aspirant.'}
                </div>
                <h1
                    className="display text-[color:var(--ink)]"
                    style={{ fontSize: 'clamp(48px, 6.2vw, 96px)' }}
                >
                    <span className="mask-reveal"><span style={{ animationDelay: '80ms' }}>Welcome,</span></span>&nbsp;
                    <span className="mask-reveal">
                        <span className="display-italic" style={{ animationDelay: '160ms', color: 'var(--lavender-deep)' }}>
                            {firstName}.
                        </span>
                    </span>
                    <br />
                    <span className="mask-reveal"><span style={{ animationDelay: '260ms' }}>Find&nbsp;the</span></span>&nbsp;
                    <span className="mask-reveal"><span style={{ animationDelay: '340ms' }}>room&nbsp;where</span></span>&nbsp;
                    <span className="mask-reveal">
                        <span className="display-italic" style={{ animationDelay: '420ms' }}>
                            your work belongs.
                        </span>
                    </span>
                </h1>
                <p className="text-[color:var(--ink-soft)] max-w-lg text-[15px] leading-relaxed pt-3">
                    A curated shelf of colleges, entrance exams and rituals of preparation —
                    bound together in a single folio. Search, dog-ear, annotate. Keep the
                    weeks close and the deadlines closer.
                </p>
            </div>

            {/* USER GOAL — hero card */}
            {user?.goal && (
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.6 }}
                    data-testid="user-goal-card"
                    className="relative border-l-2 border-[color:var(--lavender-deep)] bg-[color:var(--lavender)]/20 rounded-r-sm p-5"
                >
                    <div className="flex items-start gap-3">
                        <Sparkles size={16} className="text-[color:var(--lavender-deep)] mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                            <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-2">
                                YOUR NORTH STAR
                            </div>
                            <p
                                className="display-italic text-[color:var(--ink)]"
                                style={{ fontSize: 'clamp(18px, 2vw, 26px)', lineHeight: 1.25 }}
                            >
                                “{user.goal}”
                            </p>
                            <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-3">
                                — {firstName}, {user.education || 'aspirant'}
                            </div>
                        </div>
                    </div>
                    <div className="absolute -top-2 -right-2 mono text-[9px] bg-[color:var(--lavender-deep)] text-white px-2 py-0.5 rounded-sm">
                        PINNED
                    </div>
                </motion.div>
            )}

            {/* SEARCH */}
            <div className="relative">
                <form
                    onSubmit={(e) => { e.preventDefault(); runSearch(q); }}
                    className="flex items-center gap-3 border border-[color:var(--moss-deep)] rounded-sm bg-white/40 backdrop-blur px-4 py-3"
                >
                    <Search size={16} className="text-[color:var(--moss-deep)]" />
                    <input
                        data-testid="college-search"
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search Indian colleges & exams — IIT, NEET, DU, CAT… (add 'US'/'Harvard' for abroad)"
                        className="flex-1 bg-transparent outline-none placeholder:text-[color:var(--moss-deep)]/60 text-[color:var(--ink)]"
                    />
                    <button
                        type="submit"
                        data-testid="college-search-submit"
                        className="btn-lavender px-4 py-1.5 rounded-sm text-xs mono"
                    >
                        {loading ? '…' : 'Search'}
                    </button>
                </form>
                <div className="flex flex-wrap items-center gap-2 mt-3">
                    {['Engineering', 'Medicine', 'Commerce', 'Law', 'MBA'].map((f) => (
                        <button
                            key={f}
                            onClick={() => { setQ(f); runSearch(f); }}
                            className="btn-ghost-moss px-3 py-1 rounded-full text-[11px] mono"
                            data-testid={`filter-${f.toLowerCase().replace(/\s/g, '-')}`}
                        >
                            {f}
                        </button>
                    ))}
                    <span className="ml-auto mono text-[10px] text-[color:var(--moss-deep)]">
                        {region === 'india' ? '🇮🇳 India-first results' : '🌏 International'}
                    </span>
                </div>
            </div>

            {/* MARQUEE — editorial strip */}
            <div
                className="relative -mx-10 xl:-mx-14 border-y border-[color:var(--moss-deep)]/25 overflow-hidden bg-[color:var(--parchment-warm)]/60"
                aria-hidden
            >
                <div className="marquee-track py-4 text-[color:var(--moss-deep)]">
                    {Array.from({ length: 2 }).flatMap((_, k) =>
                        ['Curated', 'Annotated', 'Timed', 'Mentored', 'Bound in cloth', 'Printed with care']
                            .map((w, i) => (
                                <span
                                    key={`${k}-${i}`}
                                    className="display-italic px-8 whitespace-nowrap"
                                    style={{ fontSize: 'clamp(24px, 3vw, 36px)' }}
                                >
                                    {w} <span className="mx-4 text-[color:var(--lavender-deep)]">✦</span>
                                </span>
                            )),
                    )}
                </div>
            </div>

            {/* MANIFESTO CHAPTERS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                    ['01', 'Search', 'A finder built on rankings, cutoffs and honesty.'],
                    ['02', 'Prepare', 'Syllabi decoded, quizzes timed to the second.'],
                    ['03', 'Arrive', 'From admit letter to first week — kept together.'],
                ].map(([n, t, d], i) => (
                    <motion.div
                        key={n}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.7, delay: i * 0.1 }}
                        className="border-t border-[color:var(--moss-deep)] pt-4"
                    >
                        <div className="mono text-[10px] text-[color:var(--moss-deep)]">{n} / 03</div>
                        <div className="display text-[color:var(--ink)] text-3xl mt-2">{t}</div>
                        <p className="text-[13px] text-[color:var(--ink-soft)] mt-2 leading-relaxed">{d}</p>
                    </motion.div>
                ))}
            </div>

            {/* COLLEGE INDEX CARDS (live search results) */}
            <div className="space-y-4">
                <div className="flex items-baseline justify-between">
                    <h3 className="display text-[color:var(--ink)] text-2xl">
                        {q ? `Results for "${q}"` : 'Featured shelf'}
                    </h3>
                    <button
                        data-testid="cta-exam-prep"
                        onClick={() => goTo('exam-prep')}
                        className="mono text-[11px] text-[color:var(--moss-deep)] flex items-center gap-1 hover:text-[color:var(--lavender-deep)]"
                    >
                        Turn to Chapter II <ArrowUpRight size={12} />
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="college-results">
                    {results.map((c, i) => (
                        <motion.article
                            key={c.name}
                            initial={{ opacity: 0, y: 14, rotate: i % 2 ? 0.6 : -0.6 }}
                            animate={{ opacity: 1, y: 0, rotate: 0 }}
                            transition={{ delay: i * 0.05, duration: 0.5 }}
                            whileHover={{ y: -4, rotate: i % 2 ? 0.4 : -0.4 }}
                            className="group relative border border-[color:var(--moss-deep)]/30 rounded-sm bg-white/50 p-4 paper-lines"
                            data-testid={`college-card-${i}`}
                            style={{ boxShadow: '2px 3px 8px -3px rgba(59,68,30,0.35)' }}
                        >
                            {/* index tab */}
                            <div className="absolute -top-2 left-4 mono text-[9px] bg-[color:var(--lavender)] text-[color:var(--moss-dark)] px-2 py-0.5 rounded-b-sm">
                                {c.tag}
                            </div>
                            <div className="flex items-start justify-between mt-1">
                                <h4 className="display text-lg text-[color:var(--ink)] leading-tight">{c.name}</h4>
                                <span className="mono text-[10px] text-[color:var(--moss-deep)] whitespace-nowrap ml-2">
                                    {c.fees}
                                </span>
                            </div>
                            <div className="flex items-center gap-1 text-[11px] text-[color:var(--ink-soft)] mt-1">
                                <MapPin size={11} /> {c.location}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <span className="mono text-[9px] text-[color:var(--moss-deep)]">EXAM</span>
                                <span className="text-[11px] text-[color:var(--lavender-deep)] font-semibold">{c.exam}</span>
                            </div>
                            <div className="flex flex-wrap gap-1 pt-2">
                                {c.streams.map((s) => (
                                    <span
                                        key={s}
                                        className="text-[10px] mono px-2 py-0.5 rounded-full bg-[color:var(--lavender)]/40 text-[color:var(--moss-dark)]"
                                    >
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </motion.article>
                    ))}
                    {!loading && results.length === 0 && (
                        <div className="mono text-[11px] text-[color:var(--moss-deep)]">No matches — try another query.</div>
                    )}
                </div>
            </div>

            {/* End rule */}
            <div className="flex items-center gap-4 pt-4 border-t border-[color:var(--moss-deep)]/25">
                <GraduationCap size={16} className="text-[color:var(--moss-deep)]" />
                <span className="mono text-[10px] text-[color:var(--moss-deep)]">
                    End of Chapter I — turn the tab to continue.
                </span>
            </div>
        </div>
    );
}
